import { unref } from 'vue'

import utils from '@/plugins/utils'
import store from '@/store'

export enum ReApi {
  AUTH = 'auth',
  VALUATION = 'valuation',
}

export enum ReEndpoint {
  APPOINTMENT = 'appointment',
  REQUEST = 'request',
  AVAILABILITY = 'availability',
  PAYMENT = 'payment',
  CONTACT = 'contact',
  USER = 'user',
  ASSET = 'asset',
  ENERGY_SIMULATION = 'energy_simulation',
  USER_LIST = 'users',
  KNOWLEDGE_BASE = 'knowledgebase',
  INVOICE = 'invoice',
}

export enum ReApiVersion {
  CURRENT = 'v2',
}

export type QueryKey = Array<string | number | object>

interface QueryKeyUrlResult {
  url: string
  params: Record<string, unknown>
}

function getIdFieldName(queryKey: QueryKey): string {
  if (queryKey.length >= 5 && queryKey[2] === ReEndpoint.REQUEST && queryKey[4] === 'document')
    return 'document_ref'
  if (queryKey.length >= 3 && queryKey.length <= 4 && queryKey[2] === ReEndpoint.REQUEST)
    return 'valuation_request_ref'

  return 'id'
}

function parseQueryKey(queryKey: QueryKey) {
  const [api, version, endpoint, ...pathSegments] = cleanQueryKey(queryKey).filter(
    key => typeof key !== 'object',
  )
  return { api, version, endpoint, pathSegments }
}

function queryKeyToUrl(queryKey: QueryKey): QueryKeyUrlResult {
  const config: Record<string, unknown> = store.getters.getConfig
  const params = (queryKey.find(key => typeof key === 'object') ?? {}) as Record<string, unknown>
  Object.keys(params).forEach((k) => {
    params[k] = unref(params[k])
  })

  const { api, version, endpoint, pathSegments } = parseQueryKey(queryKey)
  const baseUrl: string
    = api === ReApi.AUTH
      ? (config.AUTH_API_URL as string)
      : api === ReApi.VALUATION
        ? (config.VALUATION_API_URL as string)
        : '/'
  if (baseUrl === '/')
    console.error(`queryKeyToUrl: Unknown API (${api}) for queryKey ${queryKey}`)

  return { url: utils.urlJoin(baseUrl, [version, endpoint, ...pathSegments]), params }
}

function cleanQueryKey(queryKey: QueryKey) {
  return queryKey ? unref(queryKey).map(unref) : []
}

function getBaseQueryKey(queryKey: QueryKey, verb: string): QueryKey | null {
  if (['POST', 'DELETE'].includes(verb))
    return null

  const { api, version, endpoint, pathSegments } = parseQueryKey(queryKey)

  // Handle PUT valuation/request/:ref/status/... etc:
  if (
    verb === 'PUT'
    && endpoint === ReEndpoint.REQUEST
    && pathSegments.length >= 1
    && ['valuer', 'status', 'borrower', 'transaction_value'].includes(pathSegments.at(-1) as string)
  ) {
    return [api, version, endpoint, ...pathSegments.slice(0, -1)]
  }

  return [api, version, endpoint, ...pathSegments]
}

function getCrudListQueryKey(queryKey: QueryKey, verb: string): QueryKey {
  if (verb === 'POST')
    return queryKey

  queryKey = getBaseQueryKey(queryKey, verb) || queryKey
  return queryKey.slice(0, -1)
}

export { cleanQueryKey, getBaseQueryKey, getCrudListQueryKey, getIdFieldName, queryKeyToUrl }
